import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { Trophy, TrendingUp, Undo2, Users, X, RotateCcw, Moon, Sun, Volume2, Share2, Clock, Copy, Check, Home, Watch, Monitor, Settings } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { QRCodeSVG } from 'qrcode.react';
import { database } from './firebase';
import { ref, set, onValue, off, update, remove, get, query, orderByChild } from 'firebase/database';
import TournoiComparison from './TournoiComparison';

// Fonctions utilitaires
const generateId = () => Math.random().toString(36).substring(2, 8).toUpperCase();
const generateAdminToken = () => Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

// Génération PIN 5 chiffres (10000-99999)
const generateWatchPIN = () => {
  return Math.floor(10000 + Math.random() * 90000).toString();
};

// Composant principal avec routing
export default function App() {
  return (
    <Routes>
      <Route path="/" element={<ConfigScreen />} />
      <Route path="/match/:matchId" element={<MatchScreen mode="spectator" />} />
      <Route path="/match/:matchId/admin/:adminToken" element={<MatchScreen mode="admin" />} />
      <Route path="/history" element={<HistoryScreen />} />
      <Route path="/tournoi" element={<TournoiComparison />} />
    </Routes>
  );
}

// ============================================
// ÉCRAN DE CONFIGURATION
// ============================================
function ConfigScreen() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') !== 'false');
  
  const [matchFormat, setMatchFormat] = useState({ 
    sets: 1,           // Par défaut : 1 set gagnant
    games: 6,          // Par défaut : 6 jeux
    tiebreak: 'normal', // Par défaut : TB 7 points
    advantage: 'none'  // Par défaut : Pas d'avantage
  });
  
  const availableColors = [
    { name: 'Rouge', class: 'bg-red-500', hex: '#ef4444' },
    { name: 'Bleu', class: 'bg-blue-500', hex: '#3b82f6' },
    { name: 'Vert', class: 'bg-green-500', hex: '#22c55e' },
    { name: 'Violet', class: 'bg-purple-500', hex: '#a855f7' },
  ];
  
  const [players, setPlayers] = useState([
    { id: 1, name: 'Joueur 1', team: 1, color: availableColors[0].class, colorHex: availableColors[0].hex },
    { id: 2, name: 'Joueur 2', team: 1, color: availableColors[1].class, colorHex: availableColors[1].hex },
    { id: 3, name: 'Joueur 3', team: 2, color: availableColors[2].class, colorHex: availableColors[2].hex },
    { id: 4, name: 'Joueur 4', team: 2, color: availableColors[3].class, colorHex: availableColors[3].hex },
  ]);

  const updatePlayerName = (id, name) => {
    setPlayers(players.map(p => p.id === id ? { ...p, name } : p));
  };

  const updatePlayerColor = (id, colorIndex) => {
    const color = availableColors[colorIndex];
    setPlayers(players.map(p => p.id === id ? { ...p, color: color.class, colorHex: color.hex } : p));
  };

  const canStartMatch = players.every(p => p.name.trim() !== '');

  const startMatch = async () => {
    if (!canStartMatch) return;

    const matchId = generateId();
    const adminToken = generateAdminToken();
    const watchCode = generateWatchPIN();
    
    const initialMatchState = {
      sets: { team1: 0, team2: 0 },
      games: { team1: 0, team2: 0 },
      points: { team1: 0, team2: 0 },
      isTiebreak: matchFormat.games === 0,
      isDeuce: false,
      advantage: null,
      matchWinner: null,
      setScores: [],
      serviceConfig: {
        serviceOrder: [],
        currentServerIndex: -1,
        totalGamesPlayed: 0
      }
    };

    const initialPlayerStats = {
      1: { winners: 0, faults: 0 },
      2: { winners: 0, faults: 0 },
      3: { winners: 0, faults: 0 },
      4: { winners: 0, faults: 0 },
    };

    const matchData = {
      id: matchId,
      adminToken,
      watchCode,
      createdAt: Date.now(),
      expiresAt: Date.now() + (2 * 60 * 60 * 1000), // Expire dans 2h
      config: {
        matchFormat,
        players: players.map(p => ({ id: p.id, name: p.name, team: p.team, color: p.color, colorHex: p.colorHex }))
      },
      matchState: initialMatchState,
      playerStats: initialPlayerStats,
      pointHistory: [],
      history: []
    };

    try {
      // Créer le match
      await set(ref(database, `matches/${matchId}`), matchData);
      
      // Créer le mapping watchCode -> matchId pour la montre
      await set(ref(database, `watchCodes/${watchCode}`), {
        matchId,
        createdAt: Date.now(),
        expiresAt: Date.now() + (2 * 60 * 60 * 1000) // 2h
      });
      
      const localHistory = JSON.parse(localStorage.getItem('matchHistory') || '[]');
      localHistory.unshift({
        matchId,
        adminToken,
        watchCode,
        createdAt: Date.now(),
        players: players.map(p => p.name).join(', ')
      });
      localStorage.setItem('matchHistory', JSON.stringify(localHistory.slice(0, 20)));

      navigate(`/match/${matchId}/admin/${adminToken}`);
    } catch (error) {
      console.error('Error creating match:', error);
      alert('Erreur lors de la création du match. Vérifiez votre connexion internet.');
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100'} p-4 flex items-center justify-center`}>
      <div className="w-full max-w-4xl relative">
        {/* Bouton Dark Mode en haut à droite */}
        <button
          onClick={() => {
            setDarkMode(!darkMode);
            localStorage.setItem('darkMode', (!darkMode).toString());
          }}
          className={`absolute top-0 right-0 z-10 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100'} p-3 rounded-full shadow-lg`}
        >
          {darkMode ? '🌞' : '🌙'}
        </button>
        
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-2xl p-6`}>
          <div className="text-center mb-6">
            <img src="/logo.png" alt="ECRAN.RE" className="mx-auto mb-3" style={{height: '80px'}} />
            <h1 className={`text-3xl font-black mb-2 ${darkMode ? 'text-white' : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600'}`}>
              PADEL STATS
            </h1>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} font-medium`}>Configuration du match</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <div className="flex items-center mb-3 justify-center">
                <Users className="text-blue-600 mr-2" size={20} />
                <h2 className={`text-xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>ÉQUIPE 1</h2>
              </div>
              <div className="space-y-3">
                {players.filter(p => p.team === 1).map((player) => (
                  <div key={player.id} className={`${darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-xl p-4 border-2`}>
                    <input
                      type="text"
                      value={player.name}
                      onChange={(e) => updatePlayerName(player.id, e.target.value)}
                      placeholder={`Joueur ${player.id}`}
                      className={`w-full px-4 py-3 border-2 rounded-lg mb-3 focus:outline-none font-bold ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                          : 'bg-white border-blue-300 text-gray-800 focus:border-blue-600'
                      }`}
                    />
                    <div className="flex gap-2">
                      {availableColors.map((color, idx) => (
                        <button
                          key={idx}
                          onClick={() => updatePlayerColor(player.id, idx)}
                          className={`w-10 h-10 rounded-full ${color.class} ${player.color === color.class ? 'ring-4 ring-blue-600 ring-offset-2' : ''} transition-all hover:scale-110`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center mb-3 justify-center">
                <Users className="text-red-600 mr-2" size={20} />
                <h2 className={`text-xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>ÉQUIPE 2</h2>
              </div>
              <div className="space-y-3">
                {players.filter(p => p.team === 2).map((player) => (
                  <div key={player.id} className={`${darkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'} rounded-xl p-4 border-2`}>
                    <input
                      type="text"
                      value={player.name}
                      onChange={(e) => updatePlayerName(player.id, e.target.value)}
                      placeholder={`Joueur ${player.id}`}
                      className={`w-full px-4 py-3 border-2 rounded-lg mb-3 focus:outline-none font-bold ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-red-500' 
                          : 'bg-white border-red-300 text-gray-800 focus:border-red-600'
                      }`}
                    />
                    <div className="flex gap-2">
                      {availableColors.map((color, idx) => (
                        <button
                          key={idx}
                          onClick={() => updatePlayerColor(player.id, idx)}
                          className={`w-10 h-10 rounded-full ${color.class} ${player.color === color.class ? 'ring-4 ring-red-600 ring-offset-2' : ''} transition-all hover:scale-110`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={`${darkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-gray-50 to-gray-100'} rounded-2xl p-6 mb-6`}>
            <h3 className={`text-lg font-bold ${darkMode ? 'text-gray-200' : 'text-gray-800'} mb-4`}>Format du match</h3>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Nombre de sets gagnants</label>
                <select
                  value={matchFormat.sets}
                  onChange={(e) => setMatchFormat({...matchFormat, sets: Number(e.target.value)})}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none font-bold ${
                    darkMode 
                      ? 'bg-gray-600 border-gray-500 text-white focus:border-indigo-400' 
                      : 'bg-white border-gray-300 text-gray-800 focus:border-indigo-600'
                  }`}
                >
                  <option value={1}>1 set gagnant</option>
                  <option value={2}>2 sets gagnants</option>
                  <option value={3}>2 sets puis Super TB</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Nombre de jeux</label>
                <select
                  value={matchFormat.games}
                  onChange={(e) => {
                    const games = Number(e.target.value);
                    const newFormat = {...matchFormat, games};
                    
                    // Auto-switch du tie-break selon le format
                    if (games === 0) {
                      // TB direct → Super TB 10 pts par défaut
                      newFormat.tiebreak = 'super';
                    } else {
                      // 4, 6, 9 jeux → TB 7 pts par défaut
                      if (matchFormat.tiebreak === 'super') {
                        newFormat.tiebreak = 'normal';
                      }
                    }
                    
                    setMatchFormat(newFormat);
                  }}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none font-bold ${
                    darkMode 
                      ? 'bg-gray-600 border-gray-500 text-white focus:border-indigo-400' 
                      : 'bg-white border-gray-300 text-gray-800 focus:border-indigo-600'
                  }`}
                >
                  <option value={0}>TB (Tie-Break direct)</option>
                  <option value={4}>4 jeux</option>
                  <option value={6}>6 jeux</option>
                  <option value={9}>9 jeux</option>
                </select>
              </div>
            </div>

            {matchFormat.games !== 0 && (
              <div className="mb-4">
                <label className={`block text-sm font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Format d'avantage</label>
                <select
                  value={matchFormat.advantage}
                  onChange={(e) => setMatchFormat({...matchFormat, advantage: e.target.value})}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none font-bold ${
                    darkMode 
                      ? 'bg-gray-600 border-gray-500 text-white focus:border-indigo-400' 
                      : 'bg-white border-gray-300 text-gray-800 focus:border-indigo-600'
                  }`}
                >
                  <option value="none">Pas d'avantage (sudden death)</option>
                  <option value="simple">Avantage simple</option>
                </select>
              </div>
            )}

            <div>
              <label className={`block text-sm font-bold ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Type de tie-break</label>
              <select
                value={matchFormat.tiebreak}
                onChange={(e) => setMatchFormat({...matchFormat, tiebreak: e.target.value})}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none font-bold ${
                  darkMode 
                    ? 'bg-gray-600 border-gray-500 text-white focus:border-indigo-400' 
                    : 'bg-white border-gray-300 text-gray-800 focus:border-indigo-600'
                }`}
              >
                {matchFormat.games !== 0 && <option value="none">Pas de tie-break</option>}
                <option value="normal">Tie-break (7 pts)</option>
                <option value="super">Super tie-break (10 pts)</option>
              </select>
            </div>
          </div>

          <button
            onClick={startMatch}
            disabled={!canStartMatch}
            className={`w-full font-black py-5 rounded-xl shadow-2xl text-xl transition-all max-w-md mx-auto block ${
              canStartMatch
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white active:scale-95'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {canStartMatch ? '🎾 COMMENCER LE MATCH' : '⚠️ REMPLIR TOUS LES NOMS'}
          </button>

          <button
            onClick={() => navigate('/history')}
            className={`w-full mt-4 font-bold py-3 rounded-xl transition-all flex items-center justify-center ${
              darkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <Clock className="mr-2" size={20} />
            Voir l'historique
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// ÉCRAN DE MATCH (Admin ou Spectateur)
// ============================================
function MatchScreen({ mode }) {
  const { matchId, adminToken } = useParams();
  const navigate = useNavigate();
  
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // Menu hamburger mobile
  const [showSetDetails, setShowSetDetails] = useState(false); // Panneau détail sets
  
  // Mode affichage terrain - NOUVEAU
  const [displayMode, setDisplayMode] = useState(false); // false = admin, true = affichage
  const [showSuggestion, setShowSuggestion] = useState(false);
  
  // Thème et sons
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') !== 'false');
  
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('soundEnabled');
    return saved ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('soundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  // Charger les données depuis Firebase
  useEffect(() => {
    const matchRef = ref(database, `matches/${matchId}`);
    
    onValue(matchRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setMatchData(data);
        // Vérifier si l'utilisateur est admin
        if (mode === 'admin' && adminToken === data.adminToken) {
          setIsAdmin(true);
        }
      }
      setLoading(false);
    });

    return () => off(matchRef);
  }, [matchId, adminToken, mode]);

  const playWinnerSound = () => {
    if (!soundEnabled) return;
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
    
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const playFaultSound = () => {
    if (!soundEnabled) return;
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 200;
    oscillator.type = 'sawtooth';
    gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
    
    if (navigator.vibrate) {
      navigator.vibrate([50, 50, 50]);
    }
  };

  const copySpectatorLink = () => {
    const link = `${window.location.origin}/match/${matchId}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Fonctions de jeu (admin seulement)
  const getPointDisplay = () => {
    if (!matchData) return '';
    const { matchState } = matchData;
    
    if (matchState.isTiebreak) {
      return `${matchState.points.team1} - ${matchState.points.team2}`;
    }

    const pointLabels = ['0', '15', '30', '40'];
    
    if (matchState.isDeuce && matchState.advantage) {
      return matchState.advantage === 'team1' ? 'A - 40' : '40 - A';
    }

    if (matchState.isDeuce && !matchState.advantage) {
      return '40 - 40';
    }

    const p1 = pointLabels[matchState.points.team1] || '40';
    const p2 = pointLabels[matchState.points.team2] || '40';
    
    return `${p1} - ${p2}`;
  };

  const getTeamPercentage = (playerId, statType) => {
    if (!matchData) return 0;
    const { config, playerStats } = matchData;
    const player = config.players.find(p => p.id === playerId);
    const teamPlayers = config.players.filter(p => p.team === player.team);
    
    const totalTeam = teamPlayers.reduce((sum, p) => sum + playerStats[p.id][statType], 0);
    
    if (totalTeam === 0) return 0;
    
    const playerValue = playerStats[playerId][statType];
    return Math.round((playerValue / totalTeam) * 100);
  };

  const getTiebreakGamesThreshold = () => {
    if (!matchData) return null;
    const { config } = matchData;
    if (config.matchFormat.tiebreak === 'none') return null;
    if (config.matchFormat.games === 0) return 0;
    if (config.matchFormat.games === 4) return 3;
    if (config.matchFormat.games === 6) return 6;
    if (config.matchFormat.games === 9) return 8;
    return null;
  };

  const getCurrentServer = () => {
    if (!matchData) return null;
    const { matchState } = matchData;
    const serviceConfig = matchState?.serviceConfig || { serviceOrder: [], currentServerIndex: -1, totalGamesPlayed: 0 };
    const serviceOrder = Array.isArray(serviceConfig.serviceOrder) ? serviceConfig.serviceOrder : [];
    
    // CAS 1 : Super TB direct - Après le 1er point, demander le 2ème serveur
    if (matchState?.isTiebreak && serviceOrder.length === 1) {
      const totalPoints = (matchState?.points?.team1 || 0) + (matchState?.points?.team2 || 0);
      if (totalPoints > 0) {
        return null;
      }
    }
    
    // CAS 2 : Jeux normaux - Après le JEU 1, demander confirmation du 2ème serveur
    if (!matchState?.isTiebreak && serviceOrder.length === 1 && serviceConfig.totalGamesPlayed >= 1) {
      return null;
    }
    
    const result = (serviceConfig.currentServerIndex === -1 || serviceOrder.length === 0) 
      ? null 
      : serviceOrder[serviceConfig.currentServerIndex];
    return result;
  };

  const handleServiceSelection = async (playerId) => {
    if (!isAdmin || !matchData) {
      return;
    }
    const { config, matchState } = matchData;
    
    if (!config || !config.players) {
      return;
    }
    
    const serviceConfig = matchState?.serviceConfig || { serviceOrder: [], currentServerIndex: -1, totalGamesPlayed: 0 };
    const serviceOrder = Array.isArray(serviceConfig.serviceOrder) ? serviceConfig.serviceOrder : [];
    
    
    // VÉRIFICATION : Si on clique sur le serveur actuel, ne rien faire
    const currentServer = serviceOrder[serviceConfig.currentServerIndex];
    if (currentServer === playerId) {
      return;
    }
    
    // CAS 1 : PREMIÈRE SÉLECTION (aucun ordre existant)
    if (serviceOrder.length === 0) {
      const updateData = {
        matchState: {
          ...matchState,
          serviceConfig: { 
            serviceOrder: [playerId],
            currentServerIndex: 0, 
            totalGamesPlayed: 0 
          }
        }
      };
      await update(ref(database, `matches/${matchId}`), updateData);
      return;
    }
    
    // CAS 2 : DEUXIÈME SÉLECTION (un seul joueur dans l'ordre)
    if (serviceOrder.length === 1) {
      const firstServer = serviceOrder[0];
      const firstPlayer = config.players.find(p => p.id === firstServer);
      const secondPlayer = config.players.find(p => p.id === playerId);
      
      // Vérifier que c'est l'équipe adverse
      if (firstPlayer.team === secondPlayer.team) {
        alert('Le 2ème serveur doit être de l\'équipe adverse !');
        return;
      }
      
      // Calculer l'ordre complet
      const team1Players = config.players.filter(p => p.team === firstPlayer.team);
      const team2Players = config.players.filter(p => p.team === secondPlayer.team);
      
      const otherTeam1Player = team1Players.find(p => p.id !== firstServer);
      const otherTeam2Player = team2Players.find(p => p.id !== playerId);
      
      const newServiceOrder = [firstServer, playerId, otherTeam1Player.id, otherTeam2Player.id];
      
      // CORRECTION : Détection contexte pour startIndex
      // - TB avec points > 0 : index 1 (2ème serveur)
      // - Format normal après jeu 1 : index 1 (2ème serveur sélectionné doit être actif)
      // - Début de match : index 0 (1er serveur)
      const totalPoints = (matchState?.points?.team1 || 0) + (matchState?.points?.team2 || 0);
      const isTiebreak = matchState?.isTiebreak || false;
      const totalGamesPlayed = serviceConfig.totalGamesPlayed || 0;
      
      let startIndex;
      if (isTiebreak && totalPoints > 0) {
        startIndex = 1;
      } else if (!isTiebreak && totalGamesPlayed >= 1) {
        // Format normal : après jeu 1, le 2ème serveur sélectionné doit être actif
        startIndex = 1;
      } else {
        startIndex = 0;
      }
      
      
      const updateData = {
        matchState: {
          ...matchState,
          serviceConfig: { 
            serviceOrder: newServiceOrder, 
            currentServerIndex: startIndex, 
            totalGamesPlayed: 0 
          }
        }
      };
      await update(ref(database, `matches/${matchId}`), updateData);
      return;
    }
    
    // CAS 3 : CHANGEMENT MANUEL après que la rotation soit définie
    if (serviceOrder.length === 4) {
      const currentIndex = serviceOrder.indexOf(playerId);
      
      // Vérifier si le joueur cliqué existe dans l'ordre
      if (currentIndex === -1) {
        return;
      }
      
      const firstServer = serviceOrder[0];
      const firstPlayer = config.players.find(p => p.id === firstServer);
      const clickedPlayer = config.players.find(p => p.id === playerId);
      
      // Sous-cas 3A : Modifier le 2ème serveur AVANT le 2ème jeu (totalGamesPlayed < 2)
      if (serviceConfig.totalGamesPlayed < 2 && firstPlayer.team !== clickedPlayer.team && currentIndex !== 1) {
        
        // Recalculer l'ordre complet
        const team1Players = config.players.filter(p => p.team === firstPlayer.team);
        const team2Players = config.players.filter(p => p.team === clickedPlayer.team);
        
        const otherTeam1Player = team1Players.find(p => p.id !== firstServer);
        const otherTeam2Player = team2Players.find(p => p.id !== playerId);
        
        const newServiceOrder = [firstServer, playerId, otherTeam1Player.id, otherTeam2Player.id];
        
        
        const updateData = {
          matchState: {
            ...matchState,
            serviceConfig: { 
              serviceOrder: newServiceOrder, 
              currentServerIndex: serviceConfig.currentServerIndex,
              totalGamesPlayed: serviceConfig.totalGamesPlayed 
            }
          }
        };
        await update(ref(database, `matches/${matchId}`), updateData);
      } else {
        // Sous-cas 3B : Changement manuel normal - réorganiser pour continuer logiquement
        const newServiceOrder = [
          ...serviceOrder.slice(currentIndex),
          ...serviceOrder.slice(0, currentIndex)
        ];
        
        
        const updateData = {
          matchState: {
            ...matchState,
            serviceConfig: { 
              serviceOrder: newServiceOrder, 
              currentServerIndex: 0,
              totalGamesPlayed: serviceConfig.totalGamesPlayed 
            }
          }
        };
        await update(ref(database, `matches/${matchId}`), updateData);
      }
    }
  };

  const updateServerAfterGame = (newMatchState) => {
    const serviceConfig = newMatchState?.serviceConfig || { serviceOrder: [], currentServerIndex: -1, totalGamesPlayed: 0 };
    const serviceOrder = Array.isArray(serviceConfig.serviceOrder) ? serviceConfig.serviceOrder : [];
    
    // TOUJOURS incrémenter totalGamesPlayed (même si ordre incomplet)
    const newTotalGamesPlayed = serviceConfig.totalGamesPlayed + 1;
    
    // Ne pas rotationner si l'ordre n'est pas complet (4 joueurs)
    if (serviceOrder.length !== 4) {
      newMatchState.serviceConfig = { 
        ...serviceConfig, 
        serviceOrder, 
        totalGamesPlayed: newTotalGamesPlayed  // Incrémenter quand même !
      };
      return;
    }
    
    const newIndex = (serviceConfig.currentServerIndex + 1) % 4;
    
    newMatchState.serviceConfig = { 
      ...serviceConfig, 
      serviceOrder, 
      currentServerIndex: newIndex, 
      totalGamesPlayed: newTotalGamesPlayed 
    };
  };

  const updateServerInTiebreak = (newMatchState) => {
    const serviceConfig = newMatchState?.serviceConfig || { serviceOrder: [], currentServerIndex: -1, totalGamesPlayed: 0 };
    const serviceOrder = Array.isArray(serviceConfig.serviceOrder) ? serviceConfig.serviceOrder : [];
    if (serviceOrder.length !== 4) {
      return;
    }
    
    const totalPoints = newMatchState.points.team1 + newMatchState.points.team2;
    
    // CHANGEMENT : Alterner après les points IMPAIRS (1, 3, 5, 7...)
    // Point 1 → changement → Point 2-3 servis par le suivant
    if (totalPoints >= 1 && totalPoints % 2 === 1) {
      const newIndex = (serviceConfig.currentServerIndex + 1) % 4;
      
      newMatchState.serviceConfig = { 
        ...serviceConfig, 
        serviceOrder, 
        currentServerIndex: newIndex 
      };
    }
  };

  const addPoint = async (playerId, isWinner) => {
    if (!isAdmin || !matchData) return;

    // Jouer le son
    if (isWinner) {
      playWinnerSound();
    } else {
      playFaultSound();
    }

    const { config, matchState, playerStats, pointHistory = [], history = [] } = matchData;
    const player = config.players.find(p => p.id === playerId);
    const playerTeam = player.team === 1 ? 'team1' : 'team2';
    
    // Sauvegarder l'état actuel pour annulation
    const currentState = {
      matchState: JSON.parse(JSON.stringify(matchState)),
      playerStats: JSON.parse(JSON.stringify(playerStats)),
      pointHistory: [...pointHistory],
    };
    const newHistory = [...history, currentState];

    // Si faute, le point va à l'adversaire
    const team = isWinner ? playerTeam : (playerTeam === 'team1' ? 'team2' : 'team1');

    // Mettre à jour les stats
    const newPlayerStats = JSON.parse(JSON.stringify(playerStats));
    if (isWinner) {
      newPlayerStats[playerId].winners += 1;
    } else {
      newPlayerStats[playerId].faults += 1;
    }

    let newMatchState = JSON.parse(JSON.stringify(matchState));
    newMatchState.points[team] += 1;

    // LOGIQUE TIE-BREAK
    if (newMatchState.isTiebreak) {
      // Déterminer le nombre de points pour le tie-break
      let tiebreakTarget;
      if (config.matchFormat.sets === 3 && newMatchState.sets.team1 === 1 && newMatchState.sets.team2 === 1) {
        // Format "2 sets puis Super TB" : toujours 10 points au Super TB final
        tiebreakTarget = 10;
      } else {
        // Format normal : selon la config
        tiebreakTarget = config.matchFormat.tiebreak === 'super' ? 10 : 7;
      }
      
      const diff = Math.abs(newMatchState.points.team1 - newMatchState.points.team2);
      
      if (newMatchState.points[team] >= tiebreakTarget && diff >= 2) {
        // Gagner le tie-break
        
        if (config.matchFormat.games === 0) {
          // TB DIRECT : Gagner le TB = gagner le set directement (pas de jeux comptés)
          // Sauvegarder le score du TB
          const setScore = {
            team1: newMatchState.points.team1,
            team2: newMatchState.points.team2,
            isTiebreak: true
          };
          newMatchState.setScores = [...(newMatchState.setScores || []), setScore];
          
          newMatchState.sets[team] += 1;
          newMatchState.games = { team1: 0, team2: 0 };
          newMatchState.points = { team1: 0, team2: 0 };
          
          // Vérifier format "2 sets puis Super TB"
          if (config.matchFormat.sets === 3 && newMatchState.sets.team1 === 1 && newMatchState.sets.team2 === 1) {
            // Égalité 1-1 → Super TB direct avec RESET de rotation
            newMatchState.isTiebreak = true;
            newMatchState.serviceConfig = {
              serviceOrder: [],
              currentServerIndex: -1,
              totalGamesPlayed: 0
            };
          } else if (config.matchFormat.sets === 3 && newMatchState.sets[team] >= 2) {
            // Format "2 sets puis Super TB" : 2 sets gagnés = victoire
            newMatchState.matchWinner = team;
          } else if (newMatchState.sets[team] >= config.matchFormat.sets) {
            // Formats normaux
            newMatchState.matchWinner = team;
          } else {
            // Nouveau set en TB direct
            newMatchState.isTiebreak = true;
            
            // IMPORTANT : Continuer la rotation au début du nouveau set
            updateServerAfterGame(newMatchState);
          }
        } else {
          // TB NORMAL (4, 6, 9 jeux) : Incrémenter les jeux du gagnant puis gagner le set
          // Score final sera : 4-3, 7-6, ou 9-8
          newMatchState.games[team] += 1;
          newMatchState.points = { team1: 0, team2: 0 };
          
          // Sauvegarder le score du set (en jeux)
          const setScore = {
            team1: newMatchState.games.team1,
            team2: newMatchState.games.team2,
            isTiebreak: true
          };
          newMatchState.setScores = [...(newMatchState.setScores || []), setScore];
          
          // Gagner le TB = gagner le set
          newMatchState.sets[team] += 1;
          newMatchState.games = { team1: 0, team2: 0 };
          
          // Vérifier format "2 sets puis Super TB"
          if (config.matchFormat.sets === 3 && newMatchState.sets.team1 === 1 && newMatchState.sets.team2 === 1) {
            // Égalité 1-1 → Super TB à 10 avec RESET de rotation
            newMatchState.isTiebreak = true;
            newMatchState.points = { team1: 0, team2: 0 };
            newMatchState.serviceConfig = {
              serviceOrder: [],
              currentServerIndex: -1,
              totalGamesPlayed: 0
            };
          } else if (config.matchFormat.sets === 3 && newMatchState.sets[team] >= 2) {
            // Format "2 sets puis Super TB" : 2 sets gagnés = victoire (après Super TB)
            newMatchState.matchWinner = team;
          } else if (newMatchState.sets[team] >= config.matchFormat.sets) {
            // Formats normaux (1 ou 2 sets gagnants)
            newMatchState.matchWinner = team;
          } else {
            // Nouveau set en mode normal (0-0)
            newMatchState.isTiebreak = false;
          }
        }
      }
    } else {
      // LOGIQUE JEU NORMAL
      newMatchState.isDeuce = false;
      newMatchState.advantage = null;

      if (newMatchState.points.team1 >= 3 && newMatchState.points.team2 >= 3) {
        newMatchState.isDeuce = true;

        if (config.matchFormat.advantage === 'none') {
          if (newMatchState.points[team] >= 4) {
            newMatchState.games[team] += 1;
            newMatchState.points = { team1: 0, team2: 0 };
            newMatchState.isDeuce = false;
          }
        } else {
          const diff = newMatchState.points.team1 - newMatchState.points.team2;
          if (Math.abs(diff) === 1) {
            newMatchState.advantage = diff > 0 ? 'team1' : 'team2';
          } else if (Math.abs(diff) >= 2) {
            newMatchState.games[team] += 1;
            newMatchState.points = { team1: 0, team2: 0 };
            newMatchState.isDeuce = false;
            newMatchState.advantage = null;
          }
        }
      } else {
        if (newMatchState.points[team] >= 4) {
          newMatchState.games[team] += 1;
          newMatchState.points = { team1: 0, team2: 0 };
        }
      }

      // Vérifier fin de jeu et tie-break
      if (newMatchState.points.team1 === 0 && newMatchState.points.team2 === 0) {
        const tiebreakThreshold = getTiebreakGamesThreshold();
        
        // AVEC TIE-BREAK : vérifier si on atteint le seuil TB
        if (config.matchFormat.tiebreak !== 'none' && tiebreakThreshold !== null && 
            newMatchState.games.team1 === tiebreakThreshold && 
            newMatchState.games.team2 === tiebreakThreshold) {
          // Déclencher le tie-break (3-3, 6-6, 8-8)
          newMatchState.isTiebreak = true;
          
          // IMPORTANT : Faire avancer le serveur au début du TB
          // Le premier service du TB est fait par le joueur qui DEVRAIT servir le prochain jeu
          updateServerAfterGame(newMatchState);
        } else {
          // Vérifier si le set est terminé
          const gameDiff = Math.abs(newMatchState.games.team1 - newMatchState.games.team2);
          
          if (config.matchFormat.tiebreak === 'none') {
            // SANS TIE-BREAK : Premier à N jeux gagne (peu importe l'écart)
            if (newMatchState.games[team] >= config.matchFormat.games) {
              // Sauvegarder le score du set
              const setScore = {
                team1: newMatchState.games.team1,
                team2: newMatchState.games.team2,
                isTiebreak: false
              };
              newMatchState.setScores = [...(newMatchState.setScores || []), setScore];
              
              newMatchState.sets[team] += 1;
              newMatchState.games = { team1: 0, team2: 0 };
              
              // Vérifier format "2 sets puis Super TB"
              if (config.matchFormat.sets === 3 && newMatchState.sets.team1 === 1 && newMatchState.sets.team2 === 1) {
                // Égalité 1-1 → Super TB à 10 avec RESET de rotation
                newMatchState.isTiebreak = true;
                newMatchState.points = { team1: 0, team2: 0 };
                newMatchState.serviceConfig = {
                  serviceOrder: [],
                  currentServerIndex: -1,
                  totalGamesPlayed: 0
                };
              } else if (config.matchFormat.sets === 3 && newMatchState.sets[team] >= 2) {
                // Format "2 sets puis Super TB" : 2 sets gagnés = victoire
                newMatchState.matchWinner = team;
              } else if (newMatchState.sets[team] >= config.matchFormat.sets) {
                // Formats normaux
                newMatchState.matchWinner = team;
              } else {
                // Nouveau set en mode normal (impossible en mode TB direct sans TB)
                newMatchState.isTiebreak = false;
              }
            }
          } else {
            // AVEC TIE-BREAK : Gagner avec 2 d'écart AVANT le seuil TB
            if (newMatchState.games[team] >= config.matchFormat.games && gameDiff >= 2) {
              // Sauvegarder le score du set
              const setScore = {
                team1: newMatchState.games.team1,
                team2: newMatchState.games.team2,
                isTiebreak: false
              };
              newMatchState.setScores = [...(newMatchState.setScores || []), setScore];
              
              newMatchState.sets[team] += 1;
              newMatchState.games = { team1: 0, team2: 0 };
              
              // Vérifier format "2 sets puis Super TB"
              if (config.matchFormat.sets === 3 && newMatchState.sets.team1 === 1 && newMatchState.sets.team2 === 1) {
                // Égalité 1-1 → Super TB à 10 avec RESET de rotation
                newMatchState.isTiebreak = true;
                newMatchState.points = { team1: 0, team2: 0 };
                newMatchState.serviceConfig = {
                  serviceOrder: [],
                  currentServerIndex: -1,
                  totalGamesPlayed: 0
                };
              } else if (config.matchFormat.sets === 3 && newMatchState.sets[team] >= 2) {
                // Format "2 sets puis Super TB" : 2 sets gagnés = victoire
                newMatchState.matchWinner = team;
              } else if (newMatchState.sets[team] >= config.matchFormat.sets) {
                // Formats normaux
                newMatchState.matchWinner = team;
              } else {
                // Nouveau set : TB direct reste en TB, autres formats en mode normal
                if (config.matchFormat.games === 0) {
                  newMatchState.isTiebreak = true;
                  
                  // IMPORTANT : Continuer la rotation au début du nouveau set
                  updateServerAfterGame(newMatchState);
                } else {
                  newMatchState.isTiebreak = false;
                }
              }
            }
          }
        }
      }
    }

    // Mettre à jour l'historique des points
    const newPointHistory = [...pointHistory, {
      point: pointHistory.length + 1,
      ...config.players.reduce((acc, p) => ({
        ...acc,
        [p.name]: newPlayerStats[p.id].winners - newPlayerStats[p.id].faults
      }), {})
    }];

    const gameJustEnded = (newMatchState.points.team1 === 0 && newMatchState.points.team2 === 0 && matchState.points.team1 + matchState.points.team2 > 0);
    
    if (newMatchState.isTiebreak) {
      updateServerInTiebreak(newMatchState);
    } else if (gameJustEnded) {
      updateServerAfterGame(newMatchState);
    }

    // Sauvegarder dans Firebase
    try {
      await update(ref(database, `matches/${matchId}`), {
        matchState: newMatchState,
        playerStats: newPlayerStats,
        pointHistory: newPointHistory,
        history: newHistory,
        updatedAt: Date.now()
      });
    } catch (error) {
      console.error('Error updating match:', error);
      alert('Erreur lors de la mise à jour. Vérifiez votre connexion.');
    }
  };

  const undoLastPoint = async () => {
    if (!isAdmin || !matchData || !matchData.history || matchData.history.length === 0) return;

    const { history } = matchData;
    const lastState = history[history.length - 1];
    const newHistory = history.slice(0, -1);

    try {
      // Vérifier que lastState contient bien toutes les données nécessaires
      if (!lastState || !lastState.matchState || !lastState.playerStats) {
        console.error('Invalid state in history:', lastState);
        alert('Erreur: état invalide dans l\'historique.');
        return;
      }

      await update(ref(database, `matches/${matchId}`), {
        matchState: lastState.matchState,
        playerStats: lastState.playerStats,
        pointHistory: lastState.pointHistory || [],
        history: newHistory,
        updatedAt: Date.now()
      });
    } catch (error) {
      console.error('Error undoing point:', error);
      alert('Erreur lors de l\'annulation: ' + error.message);
    }
  };

  const resetMatch = () => {
    if (!isAdmin) return;
    if (window.confirm('Voulez-vous vraiment recommencer une nouvelle partie ?')) {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-slate-900'}`}>
        <div className="text-white text-2xl font-bold animate-pulse">Chargement...</div>
      </div>
    );
  }

  if (!matchData) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${darkMode ? 'bg-gray-900' : 'bg-slate-900'}`}>
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Match introuvable</h1>
          <p className="text-gray-600 mb-6">Ce match n'existe pas ou a été supprimé.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 flex items-center justify-center mx-auto"
          >
            <Home className="mr-2" size={20} />
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const { config, matchState, playerStats, pointHistory = [] } = matchData;

  return (
    <>
      {displayMode ? (
        <DisplayModeScreen
          matchData={matchData}
          darkMode={darkMode}
          onBackToAdmin={() => setDisplayMode(false)}
        />
      ) : (
        <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-black' : 'bg-gradient-to-br from-slate-900 via-gray-900 to-zinc-900'} p-2 sm:p-4 flex flex-col transition-colors duration-300`}>
      {/* Boutons de contrôle - Menu hamburger sur mobile, boutons normaux sur desktop */}
      
      {/* Bouton bascule mode affichage - ACCESSIBLE À TOUS */}
      <button
        onClick={() => setDisplayMode(!displayMode)}
        className={`hidden sm:flex fixed top-4 right-60 z-50 p-3 rounded-full shadow-lg transition-all ${
          darkMode 
            ? 'bg-indigo-700 hover:bg-indigo-600 text-white' 
            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
        }`}
        title={displayMode ? (isAdmin ? "Retour mode admin" : "Retour mode spectateur") : "Mode affichage terrain"}
      >
        {displayMode ? <Settings size={18} /> : <Monitor size={18} />}
      </button>
      
      {/* DESKTOP : 3 boutons normaux (≥ 640px) */}
      <div className="hidden sm:flex fixed top-4 right-4 z-50 gap-2">
        {isAdmin && (
          <button
            onClick={() => setShowShareModal(true)}
            className={`p-3 rounded-full shadow-lg transition-all ${
              darkMode 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
            title="Partager aux spectateurs"
          >
            <Share2 size={18} />
          </button>
        )}
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className={`p-3 rounded-full shadow-lg transition-all ${
            darkMode 
              ? 'bg-gray-800 hover:bg-gray-700 text-white' 
              : 'bg-white hover:bg-gray-100 text-gray-800'
          }`}
          title={soundEnabled ? 'Désactiver les sons' : 'Activer les sons'}
        >
          <Volume2 size={18} className={soundEnabled ? '' : 'opacity-50'} />
        </button>
        <button
          onClick={() => setDarkMode(!darkMode)}
          className={`p-3 rounded-full shadow-lg transition-all ${
            darkMode 
              ? 'bg-gray-800 hover:bg-gray-700 text-yellow-400' 
              : 'bg-white hover:bg-gray-100 text-gray-800'
          }`}
          title={darkMode ? 'Mode clair' : 'Mode sombre'}
        >
          {darkMode ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </div>

      {/* MOBILE : Menu hamburger (< 640px) */}
      <div className="sm:hidden fixed top-2 right-2 z-50">
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`p-2 rounded-full shadow-lg transition-all ${
            darkMode 
              ? 'bg-gray-800 hover:bg-gray-700 text-white' 
              : 'bg-white hover:bg-gray-100 text-gray-800'
          }`}
          title="Menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Menu déroulant */}
        {menuOpen && (
          <div className={`absolute top-12 right-0 rounded-xl shadow-2xl overflow-hidden ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Code montre - NOUVEAU */}
            {isAdmin && matchData.watchCode && (
              <div className={`w-full px-4 py-3 flex items-center gap-3 border-b ${
                darkMode ? 'border-gray-700 text-white' : 'border-gray-100 text-gray-800'
              }`}>
                <Watch size={18} className="text-purple-500" />
                <span className="font-medium">Code: {matchData.watchCode}</span>
              </div>
            )}
            
            {isAdmin && (
              <button
                onClick={() => {
                  setShowShareModal(true);
                  setMenuOpen(false);
                }}
                className={`w-full px-4 py-3 flex items-center gap-3 transition-all ${
                  darkMode 
                    ? 'hover:bg-gray-700 text-white' 
                    : 'hover:bg-gray-100 text-gray-800'
                }`}
              >
                <Share2 size={18} className="text-green-500" />
                <span className="font-medium">Partager</span>
              </button>
            )}
            
            {/* Option partage pour spectateur - NOUVEAU */}
            {!isAdmin && (
              <button
                onClick={() => {
                  const spectatorUrl = `${window.location.origin}/${matchId}`;
                  navigator.clipboard.writeText(spectatorUrl);
                  alert('Lien spectateur copié ! 📋');
                  setMenuOpen(false);
                }}
                className={`w-full px-4 py-3 flex items-center gap-3 transition-all ${
                  darkMode 
                    ? 'hover:bg-gray-700 text-white' 
                    : 'hover:bg-gray-100 text-gray-800'
                }`}
              >
                <Share2 size={18} className="text-blue-500" />
                <span className="font-medium">Partager lien spectateur</span>
              </button>
            )}
            
            {/* Option mode affichage - ACCESSIBLE À TOUS */}
            <button
              onClick={() => {
                setDisplayMode(!displayMode);
                setMenuOpen(false);
              }}
              className={`w-full px-4 py-3 flex items-center gap-3 transition-all ${
                darkMode 
                  ? 'hover:bg-gray-700 text-white' 
                  : 'hover:bg-gray-100 text-gray-800'
              }`}
            >
              {displayMode ? (
                <>
                  <Settings size={18} className="text-purple-500" />
                  <span className="font-medium">{isAdmin ? 'Retour mode admin' : 'Retour mode spectateur'}</span>
                </>
              ) : (
                <>
                  <Monitor size={18} className="text-purple-500" />
                  <span className="font-medium">Mode affichage terrain</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => {
                setSoundEnabled(!soundEnabled);
                setMenuOpen(false);
              }}
              className={`w-full px-4 py-3 flex items-center gap-3 transition-all ${
                darkMode 
                  ? 'hover:bg-gray-700 text-white' 
                  : 'hover:bg-gray-100 text-gray-800'
              }`}
            >
              <Volume2 size={18} className={soundEnabled ? 'text-blue-500' : 'text-gray-400'} />
              <span className="font-medium">{soundEnabled ? 'Sons activés' : 'Sons désactivés'}</span>
            </button>
            <button
              onClick={() => {
                setDarkMode(!darkMode);
                setMenuOpen(false);
              }}
              className={`w-full px-4 py-3 flex items-center gap-3 transition-all ${
                darkMode 
                  ? 'hover:bg-gray-700 text-white' 
                  : 'hover:bg-gray-100 text-gray-800'
              }`}
            >
              {darkMode ? (
                <>
                  <Sun size={18} className="text-yellow-400" />
                  <span className="font-medium">Mode clair</span>
                </>
              ) : (
                <>
                  <Moon size={18} className="text-indigo-500" />
                  <span className="font-medium">Mode sombre</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full">
        {/* Badge mode */}
        {!isAdmin && (
          <div className="bg-yellow-500 text-black font-bold py-2 px-4 rounded-xl mb-2 text-center text-sm">
            👀 MODE SPECTATEUR - Le score se met à jour en temps réel
          </div>
        )}
        
        {isAdmin && (
          <div className="bg-green-500 text-white font-bold py-2 px-4 rounded-xl mb-2 text-center text-sm">
            🎾 MODE ADMIN - Vous pouvez saisir les points
          </div>
        )}
        
        {/* Suggestion bascule quand montre connectée - NOUVEAU */}
        {showSuggestion && isAdmin && (
          <div className="bg-yellow-500 text-black rounded-xl p-4 mb-4 shadow-xl">
            <div className="font-bold mb-2 flex items-center gap-2 justify-center">
              <Watch size={20} />
              ⚡ Montre connectée !
            </div>
            <div className="text-sm mb-3 text-center">
              Voulez-vous basculer en mode affichage terrain ?
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => {
                  setDisplayMode(true);
                  setShowSuggestion(false);
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-green-700"
              >
                OUI, AFFICHAGE
              </button>
              <button
                onClick={() => setShowSuggestion(false)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-gray-700"
              >
                NON, GARDER ADMIN
              </button>
            </div>
          </div>
        )}

        {/* Affichage du gagnant */}
        {matchState.matchWinner && (
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl shadow-2xl p-6 mb-4 text-center">
            <img src="/logo.png" alt="ECRAN.RE" className="mx-auto mb-3" style={{height: '100px'}} />
            <h1 className="text-3xl font-black text-white mb-2">
              VICTOIRE !
            </h1>
            <p className="text-white text-xl font-bold">
              {matchState.matchWinner === 'team1' ? 'ÉQUIPE 1' : 'ÉQUIPE 2'} remporte le match !
            </p>
          </div>
        )}

        {/* Score du match - STICKY */}
        <div className={`sticky top-0 z-20 rounded-2xl shadow-2xl p-3 sm:p-6 mb-2 sm:mb-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-2 sm:mb-4 text-center">
            <div>
              <div className={`text-xs sm:text-sm font-bold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ÉQUIPE 1</div>
              <div className="flex gap-1 sm:gap-2 justify-center items-baseline">
                <span className="text-3xl sm:text-5xl font-black text-blue-600">{matchState.sets.team1}</span>
                <span className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>-</span>
                <span className="text-2xl sm:text-4xl font-bold text-blue-500">{matchState.games.team1}</span>
              </div>
            </div>
            <div>
              <div className={`text-xs sm:text-sm font-bold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ÉQUIPE 2</div>
              <div className="flex gap-1 sm:gap-2 justify-center items-baseline">
                <span className="text-3xl sm:text-5xl font-black text-red-600">{matchState.sets.team2}</span>
                <span className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>-</span>
                <span className="text-2xl sm:text-4xl font-bold text-red-500">{matchState.games.team2}</span>
              </div>
            </div>
          </div>

          <div className={`rounded-xl p-2 sm:p-4 text-center ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-gray-50 to-gray-100'}`}>
            <div className={`text-xs font-bold mb-1 sm:mb-2 uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {matchState.isTiebreak ? (
                config.matchFormat.sets === 3 && matchState.sets.team1 === 1 && matchState.sets.team2 === 1 
                  ? '🔥 SUPER TIE-BREAK' 
                  : '🔥 TIE-BREAK'
              ) : 'Points'}
            </div>
            <div className={`text-3xl sm:text-5xl font-black ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {getPointDisplay()}
            </div>
          </div>
        </div>

        {/* PANNEAU DÉTAIL DES SETS - Repliable */}
        {matchState.setScores && matchState.setScores.length > 0 && (
          <div className={`rounded-xl shadow-lg mb-2 sm:mb-4 overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Bouton pour ouvrir/fermer */}
            <button
              onClick={() => setShowSetDetails(!showSetDetails)}
              className={`w-full px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between transition-all ${
                darkMode 
                  ? 'hover:bg-gray-700 text-white' 
                  : 'hover:bg-gray-50 text-gray-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">📊</span>
                <span className="font-bold text-sm sm:text-base">
                  Détail des sets ({matchState.sets.team1}-{matchState.sets.team2})
                </span>
              </div>
              <svg 
                className={`w-5 h-5 transition-transform ${showSetDetails ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Contenu repliable */}
            {showSetDetails && (
              <div className={`px-3 sm:px-4 pb-3 sm:pb-4 pt-2 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                {/* Sets terminés */}
                {matchState.setScores.map((setScore, index) => {
                  const team1Won = setScore.team1 > setScore.team2;
                  const team2Won = setScore.team2 > setScore.team1;
                  
                  return (
                    <div 
                      key={index}
                      className={`flex items-center justify-between py-2 ${
                        index > 0 ? `border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}` : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-sm sm:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Set {index + 1}
                        </span>
                        {setScore.isTiebreak && (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                            darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'
                          }`}>
                            TB
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`font-black text-lg sm:text-xl ${
                          team1Won ? 'text-blue-600' : darkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {setScore.team1}
                        </span>
                        <span className={`font-bold ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>-</span>
                        <span className={`font-black text-lg sm:text-xl ${
                          team2Won ? 'text-red-600' : darkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {setScore.team2}
                        </span>
                        {team1Won && <span className="text-blue-600">✅</span>}
                        {team2Won && <span className="text-red-600">✅</span>}
                      </div>
                    </div>
                  );
                })}
                
                {/* Set en cours (si le match n'est pas terminé) */}
                {!matchState.matchWinner && (
                  <div 
                    className={`flex items-center justify-between py-2 border-t ${
                      darkMode ? 'border-gray-700' : 'border-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`font-bold text-sm sm:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Set {matchState.setScores.length + 1}
                      </span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-yellow-500 text-black">
                        ⚡ EN COURS
                      </span>
                      {matchState.isTiebreak && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                          darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'
                        }`}>
                          TB
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-black text-lg sm:text-xl ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {matchState.isTiebreak ? matchState.points.team1 : matchState.games.team1}
                      </span>
                      <span className={`font-bold ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>-</span>
                      <span className={`font-black text-lg sm:text-xl ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                        {matchState.isTiebreak ? matchState.points.team2 : matchState.games.team2}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Boutons d'action (admin seulement) */}
        {isAdmin && (
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-4">
            <button 
              onClick={undoLastPoint}
              disabled={!matchData.history || matchData.history.length === 0}
              className={`font-bold py-2 sm:py-3 rounded-xl shadow-lg flex items-center justify-center transition-all text-sm sm:text-base ${
                !matchData.history || matchData.history.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white active:scale-95'
              }`}
            >
              <Undo2 className="mr-1 sm:mr-2" size={18} />
              Annuler
            </button>
            <button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold py-2 sm:py-3 rounded-xl shadow-lg flex items-center justify-center active:scale-95 transition-all text-sm sm:text-base"
            >
              <Home className="mr-1 sm:mr-2" size={18} />
              Accueil
            </button>
          </div>
        )}

        {/* ÉQUIPES */}
        <div className="grid md:grid-cols-2 gap-2 sm:gap-4 mb-2 sm:mb-4 flex-1">
          {/* Équipe 1 */}
          <div className={`rounded-2xl shadow-xl p-2 sm:p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-blue-600 font-black text-base sm:text-lg mb-2 sm:mb-3 text-center">
              <Trophy className="inline mr-2" size={18} />
              ÉQUIPE 1
            </div>
            <div className="space-y-2 sm:space-y-3">
              {config.players.filter(p => p.team === 1).map(player => (
                <div key={player.id} className={`border-2 rounded-xl p-2 sm:p-3 ${darkMode ? 'border-blue-900 bg-blue-950/30' : 'border-blue-100 bg-blue-50'}`}>
                  <div className="flex items-center mb-2 sm:mb-3 justify-center">
                    {getCurrentServer() === player.id && <span className="text-2xl mr-2">🎾</span>}
                    <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full ${player.color} mr-1 sm:mr-2`}></div>
                    <span className={`font-bold text-sm sm:text-base ${darkMode ? 'text-white' : 'text-gray-800'}`}>{player.name}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-1 sm:gap-2 mb-1 sm:mb-2">
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-black text-green-600 mb-0.5 sm:mb-1">
                        {playerStats[player.id].winners}
                        <span className={`text-xs sm:text-sm font-bold ml-1 sm:ml-2 ${darkMode ? 'text-gray-300' : 'text-black'}`}>
                          ({getTeamPercentage(player.id, 'winners')}%)
                        </span>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-black text-red-600 mb-0.5 sm:mb-1">
                        {playerStats[player.id].faults}
                        <span className={`text-xs sm:text-sm font-bold ml-1 sm:ml-2 ${darkMode ? 'text-gray-300' : 'text-black'}`}>
                          ({getTeamPercentage(player.id, 'faults')}%)
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <div className="flex items-center justify-center mb-2">
                      <button
                        onClick={() => handleServiceSelection(player.id)}
                        disabled={matchState.matchWinner}
                        className={`px-4 py-2 rounded-lg font-bold transition-all text-sm ${
                          getCurrentServer() === player.id
                            ? 'bg-blue-600 text-white'
                            : (darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800')
                        } ${matchState.matchWinner ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {getCurrentServer() === player.id ? '🎾 Service' : 'Définir service'}
                      </button>
                    </div>
                  )}
                  
                  {isAdmin && (
                    <div className="grid grid-cols-2 gap-1 sm:gap-2">
                      <button 
                        onClick={() => addPoint(player.id, true)}
                        disabled={matchState.matchWinner}
                        className={`font-bold py-3 sm:py-4 rounded-lg shadow-md transition-all text-sm sm:text-base ${
                          matchState.matchWinner
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white active:scale-95'
                        }`}
                      >
                        ✓ WINNER
                      </button>
                      <button 
                        onClick={() => addPoint(player.id, false)}
                        disabled={matchState.matchWinner}
                        className={`font-bold py-3 sm:py-4 rounded-lg shadow-md transition-all text-sm sm:text-base ${
                          matchState.matchWinner
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white active:scale-95'
                        }`}
                      >
                        ✗ FAUTE
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Équipe 2 */}
          <div className={`rounded-2xl shadow-xl p-2 sm:p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-red-600 font-black text-base sm:text-lg mb-2 sm:mb-3 text-center">
              <Trophy className="inline mr-2" size={18} />
              ÉQUIPE 2
            </div>
            <div className="space-y-2 sm:space-y-3">
              {config.players.filter(p => p.team === 2).map(player => (
                <div key={player.id} className={`border-2 rounded-xl p-2 sm:p-3 ${darkMode ? 'border-red-900 bg-red-950/30' : 'border-red-100 bg-red-50'}`}>
                  <div className="flex items-center mb-2 sm:mb-3 justify-center">
                    {getCurrentServer() === player.id && <span className="text-2xl mr-2">🎾</span>}
                    <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full ${player.color} mr-1 sm:mr-2`}></div>
                    <span className={`font-bold text-sm sm:text-base ${darkMode ? 'text-white' : 'text-gray-800'}`}>{player.name}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-1 sm:gap-2 mb-1 sm:mb-2">
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-black text-green-600 mb-0.5 sm:mb-1">
                        {playerStats[player.id].winners}
                        <span className={`text-xs sm:text-sm font-bold ml-1 sm:ml-2 ${darkMode ? 'text-gray-300' : 'text-black'}`}>
                          ({getTeamPercentage(player.id, 'winners')}%)
                        </span>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-black text-red-600 mb-0.5 sm:mb-1">
                        {playerStats[player.id].faults}
                        <span className={`text-xs sm:text-sm font-bold ml-1 sm:ml-2 ${darkMode ? 'text-gray-300' : 'text-black'}`}>
                          ({getTeamPercentage(player.id, 'faults')}%)
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <div className="flex items-center justify-center mb-2">
                      <button
                        onClick={() => handleServiceSelection(player.id)}
                        disabled={matchState.matchWinner}
                        className={`px-4 py-2 rounded-lg font-bold transition-all text-sm ${
                          getCurrentServer() === player.id
                            ? 'bg-blue-600 text-white'
                            : (darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800')
                        } ${matchState.matchWinner ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {getCurrentServer() === player.id ? '🎾 Service' : 'Définir service'}
                      </button>
                    </div>
                  )}
                  
                  {isAdmin && (
                    <div className="grid grid-cols-2 gap-1 sm:gap-2">
                      <button 
                        onClick={() => addPoint(player.id, true)}
                        disabled={matchState.matchWinner}
                        className={`font-bold py-3 sm:py-4 rounded-lg shadow-md transition-all text-sm sm:text-base ${
                          matchState.matchWinner
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white active:scale-95'
                        }`}
                      >
                        ✓ WINNER
                      </button>
                      <button 
                        onClick={() => addPoint(player.id, false)}
                        disabled={matchState.matchWinner}
                        className={`font-bold py-3 sm:py-4 rounded-lg shadow-md transition-all text-sm sm:text-base ${
                          matchState.matchWinner
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white active:scale-95'
                        }`}
                      >
                        ✗ FAUTE
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Graphique de momentum */}
        {pointHistory && pointHistory.length > 0 && (
          <div className={`rounded-2xl shadow-xl p-4 mb-2 sm:mb-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`font-black text-lg mb-3 flex items-center justify-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              <TrendingUp className="mr-2 text-purple-600" size={20} />
              MOMENTUM
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={pointHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="point" stroke={darkMode ? '#9ca3af' : '#6b7280'} />
                <YAxis stroke={darkMode ? '#9ca3af' : '#6b7280'} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: darkMode ? '#1f2937' : '#ffffff',
                    border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                    color: darkMode ? '#ffffff' : '#000000'
                  }}
                />
                <Legend />
                {config.players.map((player) => (
                  <Line
                    key={player.id}
                    type="monotone"
                    dataKey={player.name}
                    stroke={player.colorHex}
                    strokeWidth={3}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* BOUTON RESET (admin seulement) */}
        {isAdmin && (
          <div className="mt-auto pt-2 sm:pt-4">
            <button
              onClick={resetMatch}
              className={`w-full font-bold py-3 sm:py-4 rounded-xl shadow-xl flex items-center justify-center active:scale-95 transition-all text-sm sm:text-base ${
                darkMode 
                  ? 'bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white'
                  : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white'
              }`}
            >
              <RotateCcw className="mr-1 sm:mr-2" size={18} />
              RESET (Nouvelle partie)
            </button>
          </div>
        )}
      </div>

      {/* Modal de partage avec QR Code */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Partager le match</h2>
              <button onClick={() => setShowShareModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="text-center mb-6">
              <p className="text-sm text-gray-600 mb-4">Scannez ce QR code pour suivre le match en direct :</p>
              <div className="bg-white p-4 inline-block rounded-lg border-2 border-gray-200">
                <QRCodeSVG 
                  value={`${window.location.origin}/match/${matchId}`}
                  size={256}
                />
              </div>
            </div>
            
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              <p className="text-xs font-bold text-gray-600 mb-1">Lien spectateurs :</p>
              <p className="text-sm break-all font-mono mb-2">{window.location.origin}/match/{matchId}</p>
              <button
                onClick={copySpectatorLink}
                className={`w-full py-2 rounded-lg font-bold transition-all flex items-center justify-center ${
                  linkCopied 
                    ? 'bg-green-600 text-white' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {linkCopied ? (
                  <>
                    <Check className="mr-2" size={18} />
                    Copié !
                  </>
                ) : (
                  <>
                    <Copy className="mr-2" size={18} />
                    Copier le lien
                  </>
                )}
              </button>
            </div>
            
            {/* Code montre - NOUVEAU */}
            <div className="bg-purple-50 p-4 rounded-lg mb-4 border-2 border-purple-200">
              <div className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-2">
                <Watch size={16} className="text-purple-500" />
                Code montre
              </div>
              <div className="text-center mb-2">
                <div className="text-4xl font-black text-purple-600 tracking-widest">
                  {matchData.watchCode}
                </div>
              </div>
              <div className="text-xs text-center text-gray-600">
                padel.ecran.re/watch
              </div>
              <div className="text-xs text-center text-gray-500 mt-1">
                (valide 2h)
              </div>
            </div>
            
            <button
              onClick={() => setShowShareModal(false)}
              className="w-full bg-gray-200 py-3 rounded-lg font-bold hover:bg-gray-300"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
        </div>
      )}
    </>
  );
}

// ============================================
// MODE AFFICHAGE TERRAIN
// ============================================
function DisplayModeScreen({ matchData, darkMode, onBackToAdmin }) {
  const { config, matchState, playerStats } = matchData;
  
  // Calcul des pourcentages
  const team1Players = config.players.filter(p => p.team === 1);
  const team2Players = config.players.filter(p => p.team === 2);
  
  const team1Winners = team1Players.reduce((sum, p) => sum + playerStats[p.id].winners, 0);
  const team1Faults = team1Players.reduce((sum, p) => sum + playerStats[p.id].faults, 0);
  const team2Winners = team2Players.reduce((sum, p) => sum + playerStats[p.id].winners, 0);
  const team2Faults = team2Players.reduce((sum, p) => sum + playerStats[p.id].faults, 0);
  
  const getPlayerWinnerPct = (playerId, teamWinners) => {
    return teamWinners > 0 ? Math.round((playerStats[playerId].winners / teamWinners) * 100) : 0;
  };
  
  const getPlayerFaultPct = (playerId, teamFaults) => {
    return teamFaults > 0 ? Math.round((playerStats[playerId].faults / teamFaults) * 100) : 0;
  };
  
  const getPointDisplay = () => {
    if (matchState.isTiebreak) {
      return `${matchState.points.team1}-${matchState.points.team2}`;
    }
    const points = [0, 15, 30, 40];
    if (matchState.isDeuce) {
      if (matchState.advantage === 'team1') return 'Av-40';
      if (matchState.advantage === 'team2') return '40-Av';
      return '40-40';
    }
    return `${points[matchState.points.team1] || matchState.points.team1}-${points[matchState.points.team2] || matchState.points.team2}`;
  };
  
  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-black' : 'bg-gradient-to-br from-slate-900 via-gray-900 to-zinc-900'} p-2 sm:p-4 lg:p-6 flex flex-col relative`}>
      {/* Bouton retour admin */}
      <button
        onClick={onBackToAdmin}
        className="fixed top-2 right-2 sm:top-4 sm:right-4 z-50 p-2 sm:p-3 rounded-full bg-gray-700 hover:bg-gray-600 text-white shadow-xl transition-all"
        title="Retour mode admin"
      >
        <Settings size={16} className="sm:hidden" />
        <Settings size={20} className="hidden sm:block" />
      </button>
      
      <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full">
        {/* ÉQUIPE 1 */}
        <div className="text-center mb-4 sm:mb-8 lg:mb-12">
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-black text-white mb-2 sm:mb-4 lg:mb-6 flex items-center justify-center gap-2 sm:gap-3 lg:gap-4">
            <span className="text-2xl sm:text-4xl lg:text-5xl">🔴</span>
            ÉQUIPE 1
            <span className="text-2xl sm:text-4xl lg:text-5xl">🔵</span>
          </h2>
          
          <div className="flex justify-center gap-4 sm:gap-8 lg:gap-12 mb-2 sm:mb-4 lg:mb-6">
            {team1Players.map(player => (
              <div key={player.id} className="text-white">
                <div className="text-sm sm:text-xl lg:text-2xl font-bold mb-1 sm:mb-2">{player.name}</div>
                <div className="text-xs sm:text-base lg:text-lg text-green-400 font-semibold">
                  {playerStats[player.id].winners}W ({getPlayerWinnerPct(player.id, team1Winners)}%)
                </div>
                <div className="text-xs sm:text-base lg:text-lg text-red-400 font-semibold">
                  {playerStats[player.id].faults}F ({getPlayerFaultPct(player.id, team1Faults)}%)
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-3xl sm:text-5xl lg:text-7xl font-black text-white">
            {matchState.sets.team1} SET - {matchState.games.team1} JEUX
          </div>
        </div>
        
        {/* SCORE POINTS (GÉANT) */}
        <div className="text-center my-4 sm:my-8 lg:my-16">
          <div className={`text-6xl sm:text-8xl lg:text-[12rem] font-black leading-none ${darkMode ? 'text-white' : 'text-white'}`}>
            {getPointDisplay()}
          </div>
          {matchState.isTiebreak && (
            <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-orange-400 mt-2 sm:mt-3 lg:mt-4">
              {config.matchFormat.sets === 3 && matchState.sets.team1 === 1 && matchState.sets.team2 === 1 
                ? '🔥 SUPER TIE-BREAK' 
                : '🔥 TIE-BREAK'}
            </div>
          )}
        </div>
        
        {/* ÉQUIPE 2 */}
        <div className="text-center mt-4 sm:mt-8 lg:mt-12">
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-black text-white mb-2 sm:mb-4 lg:mb-6 flex items-center justify-center gap-2 sm:gap-3 lg:gap-4">
            <span className="text-2xl sm:text-4xl lg:text-5xl">🟢</span>
            ÉQUIPE 2
            <span className="text-2xl sm:text-4xl lg:text-5xl">🟣</span>
          </h2>
          
          <div className="flex justify-center gap-4 sm:gap-8 lg:gap-12 mb-2 sm:mb-4 lg:mb-6">
            {team2Players.map(player => (
              <div key={player.id} className="text-white">
                <div className="text-sm sm:text-xl lg:text-2xl font-bold mb-1 sm:mb-2">{player.name}</div>
                <div className="text-xs sm:text-base lg:text-lg text-green-400 font-semibold">
                  {playerStats[player.id].winners}W ({getPlayerWinnerPct(player.id, team2Winners)}%)
                </div>
                <div className="text-xs sm:text-base lg:text-lg text-red-400 font-semibold">
                  {playerStats[player.id].faults}F ({getPlayerFaultPct(player.id, team2Faults)}%)
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-3xl sm:text-5xl lg:text-7xl font-black text-white">
            {matchState.sets.team2} SET - {matchState.games.team2} JEUX
          </div>
        </div>
        
        {/* Détail sets */}
        {matchState.setScores && matchState.setScores.length > 0 && (
          <div className="mt-4 sm:mt-6 lg:mt-8 text-center">
            <div className="text-base sm:text-xl lg:text-2xl font-bold text-white mb-2 sm:mb-3 lg:mb-4">📊 DÉTAIL DES SETS</div>
            <div className="flex justify-center gap-2 sm:gap-3 lg:gap-4">
              {matchState.setScores.map((setScore, index) => (
                <div key={index} className="bg-gray-800 rounded-lg px-2 py-1 sm:px-3 sm:py-2 lg:px-4 lg:py-3">
                  <div className="text-xs sm:text-sm lg:text-base text-gray-400 mb-0.5 sm:mb-1">Set {index + 1}</div>
                  <div className="text-base sm:text-xl lg:text-2xl font-black text-white">
                    {setScore.team1} - {setScore.team2}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// ÉCRAN D'HISTORIQUE
// ============================================
function HistoryScreen() {
  const navigate = useNavigate();
  const [allMatches, setAllMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminMode, setAdminMode] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') !== 'false');

  const ADMIN_PIN = 'PADEL2026';

  // Charger tous les matchs Firebase au montage
  useEffect(() => {
    loadAllMatches();
  }, []);

  const loadAllMatches = async () => {
    try {
      setLoading(true);
      const matchesRef = ref(database, 'matches');
      const q = query(matchesRef, orderByChild('createdAt'));
      const snapshot = await get(q);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        const matchesArray = Object.entries(data)
          .map(([id, match]) => ({
            id,
            ...match
          }))
          .sort((a, b) => b.createdAt - a.createdAt); // Plus récent en premier
        
        setAllMatches(matchesArray);
      } else {
        setAllMatches([]);
      }
    } catch (error) {
      console.error('Error loading matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = () => {
    if (pinInput === ADMIN_PIN) {
      setAdminMode(true);
      setShowPinDialog(false);
      setPinInput('');
      setPinError('');
    } else {
      setPinError('Code incorrect');
    }
  };

  const toggleMatchSelection = (matchId) => {
    setSelectedMatches(prev => 
      prev.includes(matchId)
        ? prev.filter(id => id !== matchId)
        : [...prev, matchId]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedMatches.length === 0) return;
    
    const confirmMsg = `Supprimer ${selectedMatches.length} match${selectedMatches.length > 1 ? 's' : ''} ?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      // Supprimer chaque match de Firebase
      for (const matchId of selectedMatches) {
        await remove(ref(database, `matches/${matchId}`));
      }
      
      // Recharger la liste
      await loadAllMatches();
      setSelectedMatches([]);
      alert('Matchs supprimés avec succès !');
    } catch (error) {
      console.error('Error deleting matches:', error);
      alert('Erreur lors de la suppression.');
    }
  };

  const handleDeleteTestMatches = async () => {
    // Filtrer les matchs avec les joueurs par défaut
    const testMatches = allMatches.filter(match => {
      if (!match.config?.players) return false;
      
      const playerNames = match.config.players.map(p => p.name);
      
      // Vérifier si tous les noms sont exactement "Joueur 1", "Joueur 2", "Joueur 3", "Joueur 4"
      return playerNames.includes('Joueur 1') && 
             playerNames.includes('Joueur 2') && 
             playerNames.includes('Joueur 3') && 
             playerNames.includes('Joueur 4');
    });

    if (testMatches.length === 0) {
      alert('Aucun match de test à supprimer.');
      return;
    }

    const confirmMsg = `Supprimer ${testMatches.length} match${testMatches.length > 1 ? 's' : ''} de test ?\n\n(avec joueurs "Joueur 1/2/3/4")`;
    if (!window.confirm(confirmMsg)) return;

    try {
      // Supprimer chaque match de test de Firebase
      for (const match of testMatches) {
        await remove(ref(database, `matches/${match.id}`));
      }
      
      // Recharger la liste
      await loadAllMatches();
      setSelectedMatches([]);
      alert(`${testMatches.length} match${testMatches.length > 1 ? 's' : ''} de test supprimé${testMatches.length > 1 ? 's' : ''} !`);
    } catch (error) {
      console.error('Error deleting test matches:', error);
      alert('Erreur lors de la suppression.');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100'} p-4`}>
      <div className="max-w-4xl mx-auto">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-2xl p-6`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className={`text-3xl font-black ${darkMode ? 'text-white' : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600'}`}>
              📜 Historique
            </h1>
            <div className="flex items-center gap-2">
              {/* Bouton Analyse */}
              <button
                onClick={() => navigate('/tournoi')}
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2"
              >
                📊 Analyse
              </button>
              
              {/* Bouton Admin */}
              <button
                onClick={() => adminMode ? setAdminMode(false) : setShowPinDialog(true)}
                className={`${adminMode ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-500 hover:bg-gray-600'} text-white px-4 py-2 rounded-lg font-bold`}
              >
                {adminMode ? '🔓 Quitter' : '🔑 Admin'}
              </button>
              
              {/* Toggle Dark Mode */}
              <button
                onClick={() => {
                  setDarkMode(!darkMode);
                  localStorage.setItem('darkMode', (!darkMode).toString());
                }}
                className={`${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} p-3 rounded-lg`}
              >
                {darkMode ? '🌞' : '🌙'}
              </button>
              
              {/* Bouton Fermer */}
              <button
                onClick={() => navigate('/')}
                className={`${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} p-3 rounded-lg`}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Boutons Admin */}
          {adminMode && (
            <div className="mb-4 space-y-2">
              {/* Bouton Supprimer sélection */}
              {selectedMatches.length > 0 && (
                <div className="flex items-center justify-between bg-red-50 border-2 border-red-300 rounded-lg p-3">
                  <span className="text-red-700 font-bold">
                    {selectedMatches.length} match{selectedMatches.length > 1 ? 's' : ''} sélectionné{selectedMatches.length > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={handleDeleteSelected}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-bold"
                  >
                    🗑️ Supprimer la sélection
                  </button>
                </div>
              )}
              
              {/* Bouton Supprimer matchs de test */}
              <div className={`flex items-center justify-between ${darkMode ? 'bg-orange-900/30 border-orange-700' : 'bg-orange-50 border-orange-300'} border-2 rounded-lg p-3`}>
                <span className={`font-bold ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                  🧹 Nettoyer les matchs de test
                </span>
                <button
                  onClick={handleDeleteTestMatches}
                  className={`${darkMode ? 'bg-orange-600 hover:bg-orange-700' : 'bg-orange-500 hover:bg-orange-600'} text-white px-4 py-2 rounded-lg font-bold`}
                >
                  Supprimer "Joueur 1/2/3/4"
                </button>
              </div>
            </div>
          )}

          {/* Liste des matchs */}
          {allMatches.length === 0 ? (
            <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <Clock size={64} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-bold">Aucun match dans l'historique</p>
              <p className="text-sm mt-2">Créez votre premier match pour le voir ici !</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allMatches.map((match) => {
                // Formater : "Joueur1 - Joueur2 vs Joueur3 - Joueur4"
                let players = 'Match sans nom';
                if (match.config?.players) {
                  const team1 = match.config.players.filter(p => p.team === 1).map(p => p.name).join(' - ');
                  const team2 = match.config.players.filter(p => p.team === 2).map(p => p.name).join(' - ');
                  players = `${team1} vs ${team2}`;
                }
                
                const date = new Date(match.createdAt).toLocaleString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });

                return (
                  <div
                    key={match.id}
                    className={`${darkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-blue-50 to-purple-50'} rounded-xl p-4 border-2 ${
                      selectedMatches.includes(match.id) 
                        ? 'border-red-500' 
                        : darkMode ? 'border-gray-600' : 'border-blue-200'
                    } ${adminMode ? '' : 'hover:border-blue-400 cursor-pointer'} transition-all`}
                    onClick={() => !adminMode && navigate(`/match/${match.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      {/* Checkbox (visible en mode admin) */}
                      {adminMode && (
                        <input
                          type="checkbox"
                          checked={selectedMatches.includes(match.id)}
                          onChange={() => toggleMatchSelection(match.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-5 h-5 text-red-500 rounded mr-3"
                        />
                      )}
                      
                      <div className="flex-1">
                        <p className={`font-bold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{players}</p>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{date}</p>
                      </div>
                      
                      {!adminMode && (
                        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700">
                          Ouvrir
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Dialog PIN */}
      {showPinDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl`}>
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              🔑 Code Admin
            </h2>
            <input
              type="password"
              value={pinInput}
              onChange={(e) => {
                setPinInput(e.target.value);
                setPinError('');
              }}
              onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
              placeholder="Entrez le code"
              className={`w-full px-4 py-3 border-2 rounded-lg mb-3 text-lg ${
                pinError 
                  ? 'border-red-500' 
                  : darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300'
              }`}
              autoFocus
            />
            {pinError && <p className="text-red-500 text-sm mb-3">{pinError}</p>}
            <div className="flex gap-3">
              <button
                onClick={handlePinSubmit}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-lg font-bold"
              >
                Valider
              </button>
              <button
                onClick={() => {
                  setShowPinDialog(false);
                  setPinInput('');
                  setPinError('');
                }}
                className={`flex-1 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-300 hover:bg-gray-400'} px-4 py-3 rounded-lg font-bold`}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
